import { useEffect, useMemo, useRef, useState } from "react";
import { sortToIndexName } from "src/components/FilterSortSidebar/hashParser";
import {
  useAlgoliaMapperProduct,
  useAlgoliaQuery,
  useProductsFromApolloCache,
} from "src/hooks";
import { IDictionary } from "src/types/dictionary";
import { IFilterValue } from "src/types/filter";
import { IAlgoliaProduct } from "src/types/product";
import { ISort } from "src/types/sort";

import { IAlgoliaResponse } from "./useAlgoliaQuery";
import { IApolloProducts } from "./useApolloProductsFromAlgolia";

interface IParams {
  input?: string;
  facetFilters?: string[] | string[][];
  page?: number;
  hitsPerPage?: number;
  selectedFilters?: IFilterValue[];
  selectedSort?: ISort;
  run?: boolean;
}

interface IProductSearch {
  query: IApolloProducts;
  stats: IAlgoliaResponse<IAlgoliaProduct>["stats"];
  loading: boolean;
}

/**
 * Get products from algolia and cache them into Apollo cache
 * Use Apollo query to retrieve them
 * @returns array of product id
 * @param input
 * @param facetFilters
 * @param page
 * @param hitsPerPage
 * @param selectedSort
 * @param selectedFilters
 * @param run
 * @param onClearProducts
 */
export const useProductsSearch = (
  {
    input,
    facetFilters: defaultFacetFilters,
    page,
    hitsPerPage,
    selectedSort,
    selectedFilters,
    run,
  }: IParams,
  onClearProducts?: () => void
): IProductSearch => {
  const indexName = sortToIndexName(selectedSort);

  // if selectedFilters is undefined use empty array ref
  const { current: empty } = useRef<IFilterValue[]>([]);
  const selectedFiltersRef = selectedFilters || empty;

  // Create dictionary from selected filters
  const { facetFilters, numericFilters } = useMemo(() => {
    const filterDictionary: IDictionary<
      IFilterValue[]
    > = selectedFiltersRef.reduce((acc, el) => {
      (acc[el.filterLabel] ?? (acc[el.filterLabel] = [])).push(el);

      return acc;
    }, {});

    // Price filter
    const numeric: string[] = [];
    if (filterDictionary["price.EUR.default"]) {
      const { filterLabel, id } = filterDictionary["price.EUR.default"][0];
      numeric.push(`${filterLabel}:${id}`);
      delete filterDictionary["price.EUR.default"];
    }

    // Standard filter
    const facet = Object.keys(
      filterDictionary
    ).map((filterValueKey: string): string[] =>
      filterDictionary[filterValueKey].map(
        (filterValue: IFilterValue): string =>
          `${filterValueKey}:${filterValue.label}`
      )
    );

    return { facetFilters: facet, numericFilters: numeric };
  }, [selectedFiltersRef]);

  // Query Algolia
  const { hits, stats, loading } = useAlgoliaQuery<IAlgoliaProduct>(
    {
      indexName,
      query: input,
      maxValuesPerFacet: 1000,
      facetFilters: [...(defaultFacetFilters ?? []), ...facetFilters],
      numericFilters,
      page,
      hitsPerPage,
      run,
    },
    [
      indexName,
      input,
      defaultFacetFilters,
      facetFilters,
      numericFilters,
      page,
      run,
    ]
  );

  const products = useAlgoliaMapperProduct(hits);

  const [productIds, setProductIds] = useState<string[]>([]);

  /**
   * Clear product list and call onClearProducts
   */
  useEffect(() => {
    setProductIds([]);
    onClearProducts?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indexName, input, facetFilters, numericFilters]);

  /**
   * Add new product to list
   */
  useEffect(() => {
    setProductIds((old) => [...old, ...products.map((val) => val.id)]);
  }, [products]);

  const query = useProductsFromApolloCache(products, productIds);

  return {
    query,
    stats,
    loading: loading || query.loading,
  };
};
